import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap';
import { calculatePositionSize, calculateKellyCriterion, formatPrice } from '../utils/riskCalculations';
import { PositionSizeStorage } from '../utils/riskStorage';

const PositionSizeCalculator = () => {
  // Initialize with stored settings or defaults
  const [formState, setFormState] = useState(() => {
    return PositionSizeStorage.loadSettings();
  });
  
  // Destructure form state for easier access
  const {
    accountSize,
    riskPercentage,
    entryPrice,
    stopLossPrice,
    direction,
    useFixedRisk,
    fixedRiskAmount,
    winRate,
    rewardRatio
  } = formState;
  
  // Results state
  const [result, setResult] = useState(null);
  const [kellyResult, setKellyResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Update form state handler (memoized to avoid dependency issues)
  const updateFormState = useCallback((field, value) => {
    const newState = { ...formState, [field]: value };
    setFormState(newState);
    // Save to localStorage
    PositionSizeStorage.saveSettings(newState);
  }, [formState]);
  
  // Update stop loss when direction changes
  useEffect(() => {
    if (direction === 'long' && stopLossPrice >= entryPrice) {
      updateFormState('stopLossPrice', Math.max(1, entryPrice * 0.95)); // Default to 5% below entry for long
    } else if (direction === 'short' && stopLossPrice <= entryPrice) {
      updateFormState('stopLossPrice', entryPrice * 1.05); // Default to 5% above entry for short
    }
  }, [direction, entryPrice, stopLossPrice, updateFormState]); // Added updateFormState to dependency array
  
  // Handle direction change from entry/stop loss values
  useEffect(() => {
    const newDirection = entryPrice > stopLossPrice ? 'long' : 'short';
    if (newDirection !== direction) {
      updateFormState('direction', newDirection);
    }
  }, [entryPrice, stopLossPrice, direction, updateFormState]); // Added updateFormState to dependency array
  
  // Calculate position size when inputs change
  const calculatePosition = () => {
    try {
      setError(null);
      
      // Validate inputs
      if (!accountSize || accountSize <= 0) {
        throw new Error("Account size must be positive");
      }
      
      if ((!riskPercentage || riskPercentage <= 0) && !useFixedRisk) {
        throw new Error("Risk percentage must be positive");
      }
      
      if (useFixedRisk && (!fixedRiskAmount || fixedRiskAmount <= 0)) {
        throw new Error("Fixed risk amount must be positive");
      }
      
      if (!entryPrice || entryPrice <= 0) {
        throw new Error("Entry price must be positive");
      }
      
      if (!stopLossPrice || stopLossPrice <= 0) {
        throw new Error("Stop loss price must be positive");
      }
      
      if (entryPrice === stopLossPrice) {
        throw new Error("Entry and stop loss prices cannot be the same");
      }
      
      // Calculate position size
      const positionResult = calculatePositionSize(
        accountSize, 
        riskPercentage, 
        entryPrice, 
        stopLossPrice, 
        useFixedRisk ? fixedRiskAmount : null
      );
      
      setResult(positionResult);
      
      // Calculate Kelly criterion if win rate and reward ratio are set
      if (winRate > 0 && winRate < 100 && rewardRatio > 0) {
        const kellyPercentage = calculateKellyCriterion(winRate, rewardRatio);
        const kellyPositionSize = parseFloat(((accountSize * (kellyPercentage / 100)) / Math.abs(entryPrice - stopLossPrice)).toFixed(8));
        
        setKellyResult({
          kellyPercentage,
          kellyPositionSize,
          kellyRiskAmount: kellyPositionSize * Math.abs(entryPrice - stopLossPrice),
          kellyRiskPercentage: (kellyPositionSize * Math.abs(entryPrice - stopLossPrice) / accountSize) * 100
        });
      }
    } catch (err) {
      setError(err.message);
      setResult(null);
      setKellyResult(null);
    }
  };
  
  // Reset form to defaults
  const handleReset = () => {
    const defaultSettings = {
      accountSize: 10000,
      riskPercentage: 1,
      entryPrice: 100,
      stopLossPrice: 95,
      direction: 'long',
      useFixedRisk: false,
      fixedRiskAmount: 100,
      winRate: 50,
      rewardRatio: 2
    };
    
    setFormState(defaultSettings);
    PositionSizeStorage.saveSettings(defaultSettings);
    setResult(null);
    setKellyResult(null);
    setError(null);
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="card-header-gradient">
        <h4 className="mb-0">
          <i className="bi bi-calculator me-2"></i>
          Position Size Calculator
        </h4>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Account Size</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0"
                    step="100"
                    value={accountSize}
                    onChange={(e) => updateFormState('accountSize', parseFloat(e.target.value))}
                  />
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Risk Type</Form.Label>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    id="risk-percentage"
                    label="% of Account"
                    checked={!useFixedRisk}
                    onChange={() => updateFormState('useFixedRisk', false)}
                    className="me-3"
                  />
                  <Form.Check
                    type="radio"
                    id="risk-fixed"
                    label="Fixed Amount"
                    checked={useFixedRisk}
                    onChange={() => updateFormState('useFixedRisk', true)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              {useFixedRisk ? (
                <Form.Group className="mb-3">
                  <Form.Label>Fixed Risk Amount</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      min="0"
                      step="10"
                      value={fixedRiskAmount}
                      onChange={(e) => updateFormState('fixedRiskAmount', parseFloat(e.target.value))}
                    />
                  </div>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Risk Percentage</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min="0.1"
                      step="0.1"
                      max="100"
                      value={riskPercentage}
                      onChange={(e) => updateFormState('riskPercentage', parseFloat(e.target.value))}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </Form.Group>
              )}
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Trade Direction</Form.Label>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    id="direction-long"
                    label="Long"
                    checked={direction === 'long'}
                    onChange={() => {
                      updateFormState('direction', 'long');
                      if (stopLossPrice >= entryPrice) {
                        updateFormState('stopLossPrice', Math.max(1, entryPrice * 0.95));
                      }
                    }}
                    className="me-3"
                  />
                  <Form.Check
                    type="radio"
                    id="direction-short"
                    label="Short"
                    checked={direction === 'short'}
                    onChange={() => {
                      updateFormState('direction', 'short');
                      if (stopLossPrice <= entryPrice) {
                        updateFormState('stopLossPrice', entryPrice * 1.05);
                      }
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entry Price</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0.0001"
                    step="0.01"
                    value={entryPrice}
                    onChange={(e) => updateFormState('entryPrice', parseFloat(e.target.value))}
                  />
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Stop Loss Price
                  <span className={`ms-2 badge ${direction === 'long' ? 'bg-danger' : 'bg-success'}`}>
                    {direction === 'long' ? 'Below Entry' : 'Above Entry'}
                  </span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0.0001"
                    step="0.00001"
                    value={stopLossPrice}
                    onChange={(e) => updateFormState('stopLossPrice', parseFloat(e.target.value))}
                    className={stopLossPrice === entryPrice ? 'is-invalid' : ''}
                  />
                </div>
                <Form.Text className={`text-${direction === 'long' ? 'danger' : 'success'}`}>
                  {direction === 'long' 
                    ? 'Stop Loss must be below Entry Price' 
                    : 'Stop Loss must be above Entry Price'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <hr className="my-4" />
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Expected Win Rate</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={winRate}
                    onChange={(e) => updateFormState('winRate', parseFloat(e.target.value))}
                  />
                  <span className="input-group-text">%</span>
                </div>
                <Form.Text className="text-muted">
                  Used for Kelly Criterion calculation
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Risk/Reward Ratio</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">1:</span>
                  <Form.Control
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={rewardRatio}
                    onChange={(e) => updateFormState('rewardRatio', parseFloat(e.target.value))}
                  />
                </div>
                <Form.Text className="text-muted">
                  Reward to risk ratio (e.g., 2 means potential reward is 2x the risk)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-flex mt-4">
            <Button variant="primary" onClick={calculatePosition} className="me-2">
              <i className="bi bi-calculate me-2"></i>
              Calculate
            </Button>
            <Button variant="outline-secondary" onClick={handleReset}>
              <i className="bi bi-arrow-repeat me-1"></i>
              Reset
            </Button>
          </div>
        </Form>
        
        {result && (
          <div className="mt-4">
            <h5 className="mb-3 position-relative">
              <span className="gradient-text">Position Size Results</span>
            </h5>
            
            <Table responsive className="table">
              <thead>
                <tr>
                  <th>Position Details</th>
                  <th>Value</th>
                  <th>Explanation</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Position Type</td>
                  <td>
                    <span className={`badge ${result.direction === 'long' ? 'signal-badge-long' : 'signal-badge-short'}`}>
                      {result.direction.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {result.direction === 'long' ? 'Buy at entry, sell at stop loss' : 'Sell at entry, buy at stop loss'}
                  </td>
                </tr>
                <tr>
                  <td>Position Size</td>
                  <td><strong>{result.positionSizeInUnits.toLocaleString(undefined, { maximumFractionDigits: 8 })}</strong> units</td>
                  <td>Number of shares/contracts to trade</td>
                </tr>
                <tr>
                  <td>Position Value</td>
                  <td>${formatPrice(result.positionValue)}</td>
                  <td>Total value of position (units × entry price)</td>
                </tr>
                <tr>
                  <td>Risk Per Unit</td>
                  <td>${formatPrice(result.riskPerUnit)}</td>
                  <td>Price difference between entry and stop loss</td>
                </tr>
                <tr>
                  <td>Total Risk</td>
                  <td>${formatPrice(result.totalRisk)}</td>
                  <td>Maximum potential loss (risk per unit × position size)</td>
                </tr>
                <tr>
                  <td>Risk Percentage</td>
                  <td>{result.actualRiskPercentage.toFixed(2)}%</td>
                  <td>Percentage of account at risk</td>
                </tr>
              </tbody>
            </Table>
            
            {kellyResult && (
              <div className="mt-4">
                <h5 className="mb-3">
                  <span className="gradient-text">Kelly Criterion Position Sizing</span>
                </h5>
                <p className="mb-3">
                  The Kelly Criterion calculates the optimal position size for maximum capital growth 
                  based on your win rate and risk/reward ratio.
                </p>
                
                <Table responsive className="table">
                  <thead>
                    <tr>
                      <th>Kelly Details</th>
                      <th>Value</th>
                      <th>Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Kelly Percentage</td>
                      <td>{kellyResult.kellyPercentage.toFixed(2)}%</td>
                      <td>Optimal percentage of capital to risk per trade</td>
                    </tr>
                    <tr>
                      <td>Kelly Position Size</td>
                      <td><strong>{kellyResult.kellyPositionSize.toLocaleString(undefined, { maximumFractionDigits: 8 })}</strong> units</td>
                      <td>Kelly-optimal position size in units</td>
                    </tr>
                    <tr>
                      <td>Kelly Risk Amount</td>
                      <td>${formatPrice(kellyResult.kellyRiskAmount)}</td>
                      <td>Total risk amount with Kelly sizing</td>
                    </tr>
                    <tr>
                      <td>Kelly Risk Percentage</td>
                      <td>{kellyResult.kellyRiskPercentage.toFixed(2)}%</td>
                      <td>Percentage of account at risk with Kelly sizing</td>
                    </tr>
                    <tr>
                      <td>Conservative Approach (Half-Kelly)</td>
                      <td>{(kellyResult.kellyPercentage / 2).toFixed(2)}%</td>
                      <td>Many traders use half-Kelly for more conservative sizing</td>
                    </tr>
                  </tbody>
                </Table>
                
                {kellyResult.kellyPercentage === 0 && (
                  <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Kelly Criterion returned zero, indicating this trade has negative expectancy with the given win rate and risk/reward ratio.
                  </Alert>
                )}
                
                {kellyResult.kellyPercentage > 25 && (
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    Full Kelly suggests a high percentage of capital ({kellyResult.kellyPercentage.toFixed(2)}%). Consider using Half-Kelly ({(kellyResult.kellyPercentage / 2).toFixed(2)}%) or Quarter-Kelly ({(kellyResult.kellyPercentage / 4).toFixed(2)}%) for more conservative risk management.
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PositionSizeCalculator;